import { getSpeakingBusy } from './speaking';
import { isAudioPlaying } from './audio';

/**
 * "El juego está frizado" — mientras suena el premio de un acierto o la
 * explicación de un error, se congela toda la pantalla de juego: si Clarisa
 * toca otra cosa mientras tanto, ese toque le gana el canal de audio al
 * sonido de premio (ver lib/audio.ts, un solo sonido por vez) y lo corta o
 * se le superpone a medio camino, antes de que llegue a escucharlo entero.
 * Pasaba sobre todo en Arrastrar: acertaba una tarjeta, tocaba la siguiente
 * antes de que terminara "¡Muy bien! ¡Perro!", y el sonido de la tarjeta
 * nueva pisaba al de premio.
 *
 * `GameShell` es quien realmente frena los toques: mientras `isGameLocked()`
 * es true pinta un overlay transparente encima de todo el juego (bar +
 * contenido). `lockGame()` la llaman `celebrate()` / `celebrateCorrect()` /
 * `answerWrong()` (lib/confetti.ts) en el momento exacto en que arrancan, así
 * que ningún juego tiene que acordarse de llamarla por su cuenta.
 *
 * No hace falta un `unlock()` explícito: en vez de que cada sitio avise
 * "ya terminé" (que hay que enganchar a mano en cada `onDone`, y es fácil
 * olvidarse de alguno — o de un camino que ni pasa por `useSay`, como el
 * `audio.play()` a mano de Spotilisa), `lockGame()` sondea si sigue sonando
 * algo — `getSpeakingBusy()` (una secuencia de `useSay`, gaps entre pasos
 * incluidos), `isAudioPlaying()` (una grabación) o `speechSynthesis.speaking`
 * (el TTS, incluido el "¡Muy bien!" suelto de `celebrate()`) — y se destraba
 * sola apenas queda todo en silencio. `minMs` cubre el arranque: el "¡Muy
 * bien!" de `celebrate('big')` tarda ~280ms en arrancar (espera a que el
 * canal esté libre), y sin un piso el sondeo podría destrabar en ese hueco.
 * `maxMs` es la red de seguridad si algo nunca avisa que terminó.
 */

let locked = false;
let session = 0;
let pollTimer = 0;
const listeners = new Set<() => void>();

export function isGameLocked(): boolean {
  return locked;
}

export function subscribeGameLock(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function setLocked(v: boolean): void {
  if (locked === v) return;
  locked = v;
  listeners.forEach((l) => l());
}

function stillBusy(): boolean {
  return getSpeakingBusy() || isAudioPlaying() ||
    (typeof window !== 'undefined' && !!window.speechSynthesis?.speaking);
}

/** Corta el freeze ya mismo, sin esperar a que el sondeo lo note. Lo usa la
 *  navegación (`useNav.ts`): al salir de una sección no tiene sentido seguir
 *  frizando una pantalla que ya no se ve. */
export function unlockGameNow(): void {
  session++; // invalida cualquier lockGame() pendiente, para que su sondeo no reviva el lock
  window.clearTimeout(pollTimer);
  setLocked(false);
}

export function lockGame(opts: { minMs?: number; maxMs?: number } = {}): void {
  const { minMs = 500, maxMs = 4000 } = opts;
  const mySession = ++session;
  const startedAt = Date.now();
  setLocked(true);

  const tick = () => {
    if (session !== mySession) return; // otro lockGame() más nuevo tomó la posta
    const elapsed = Date.now() - startedAt;
    if (elapsed >= maxMs || (elapsed >= minMs && !stillBusy())) {
      setLocked(false);
      return;
    }
    pollTimer = window.setTimeout(tick, 120);
  };
  window.clearTimeout(pollTimer);
  pollTimer = window.setTimeout(tick, 120);
}
